"use client"

import { useContext, useEffect, useState, useRef } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import EmailIcon from "@mui/icons-material/Email"
import WhatsAppIcon from "@mui/icons-material/WhatsApp"
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber"
import { Modal, Button, IconButton, CircularProgress, TextField, Box, useMediaQuery, useTheme } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"

import { abrirFacturaPdf, generarFacturaPdfBlob } from "../../context/facturaAfipHtmlGenerator.js"
import printerService from "../../context/tickeadora/printerService.js"
import { obtenerParametrosTicket } from "../../context/tickeadora/ticketUtils.js"

const ModalOpcionesFactura = ({
  open,
  handleClose,
  transaccionData,
  impuestos,
  itemsTransaccion,
  entidadParaFactura,
  usuarioParaFactura,
  tipoDeFactura,
  deDondeViene,
  afipData,
  afipPayload,
}) => {
  const { generarFacturaPDF, generarRemitoPDF, enviarFacturaWhatsApp, enviarMail , guardarComprobante, isNavbarCollapsed, updateEntidadEmail, getParametrosGlobales, getCuentaCorrienteByIdEntidad,
  } = useContext(ErpContext)
  const [textoDeExito, setTextoDeExito] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [accionActual, setAccionActual] = useState("")
  const [openEmailModal, setOpenEmailModal] = useState(false)
  const [emailValue, setEmailValue] = useState("")
  const [emailError, setEmailError] = useState("")
  const [openPhoneModal, setOpenPhoneModal] = useState(false)
  const [phoneValue, setPhoneValue] = useState("")
  const [phoneError, setPhoneError] = useState("")
  const hasAutoPrintedRef = useRef(false)
  const [usaTickeadoraActiva, setUsaTickeadoraActiva] = useState(null)
  const [parametrosTicket, setParametrosTicket] = useState(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))
  const isMobileOrTablet = isMobile || isTablet

  useEffect(() => {
    let cancelado = false

    if (!open) {
      hasAutoPrintedRef.current = false
      setUsaTickeadoraActiva(null)
      setParametrosTicket(null)
      return
    }

    ;(async () => {
      try {
        const params = await obtenerParametrosTicket(getParametrosGlobales)
        if (cancelado) return
        setParametrosTicket(params)
        const usaTickeadora = params?.usaTickeadora
        const habilitado = usaTickeadora === 1 || usaTickeadora === '1' || usaTickeadora === true
        setUsaTickeadoraActiva(habilitado)
      } catch (error) {
        if (!cancelado) {
          console.error('Error cargando configuración de tickeadora:', error)
          setUsaTickeadoraActiva(false)
          setParametrosTicket(null)
        }
      }
    })()

    return () => {
      cancelado = true
    }
  }, [open, getParametrosGlobales])

  useEffect(() => {
    if (deDondeViene == "venta" || deDondeViene == "crearVenta") {
      setTextoDeExito("¡Tu VENTA se realizó con éxito!")
    }
    if (deDondeViene == "compra") {
      setTextoDeExito("¡Tu COMPRA se realizó con éxito!")
    }
    if (deDondeViene == "presupuesto") {
      setTextoDeExito("¡Tu PRESUPUESTO se realizó con éxito!")
    }
    if (deDondeViene == "devolucion") {
      setTextoDeExito("¡Tu DEVOLUCIÓN se realizó con éxito!")
    }
  }, [deDondeViene])

  useEffect(() => {
    if (open && (deDondeViene === 'venta' || deDondeViene === 'crearVenta') && usaTickeadoraActiva === true && !hasAutoPrintedRef.current) {
      hasAutoPrintedRef.current = true
      handlePrintTicket()
    }

    if (!open || usaTickeadoraActiva === false) {
      hasAutoPrintedRef.current = false
    }
  }, [open, deDondeViene, usaTickeadoraActiva])

  const handleDownloadPdf = async () => {
    try {
      setIsLoading(true)
      setAccionActual("Generando PDF...")

      // Obtener cuenta corriente solo si es venta, crearVenta o presupuesto
      let cuentaCorrienteInfo = null
      if (deDondeViene === "venta" || deDondeViene === "crearVenta" || deDondeViene === "presupuesto") {
        try {
          const cuentas = await getCuentaCorrienteByIdEntidad(entidadParaFactura?.id)
          const cuenta = cuentas && cuentas.length > 0 ? cuentas.find((ct) => ct.idEntidad === entidadParaFactura?.id) : null
          if (cuenta) {
            cuentaCorrienteInfo = {
              saldo: cuenta.saldo,
              tieneCuentaCorriente: true
            }
          } else {
            cuentaCorrienteInfo = {
              saldo: 0,
              tieneCuentaCorriente: false
            }
          }
        } catch (error) {
          console.error("Error al obtener cuenta corriente:", error)
          cuentaCorrienteInfo = {
            saldo: 0,
            tieneCuentaCorriente: false
          }
        }
      }

      if (deDondeViene === "devolucion") {
        // ✅ NUEVA LÓGICA PARA DEVOLUCIONES
        // Generar PDF local para devoluciones
        const pdfDataUrl = await generarFacturaPDF(
          transaccionData, 
          itemsTransaccion, 
          entidadParaFactura, 
          true, // tipoDeFactura = true (local)
          impuestos, 
          deDondeViene,
          false, // returnBase64
          cuentaCorrienteInfo
        )
        const finalPdfUrl = pdfDataUrl?.data?.pdfUrl || pdfDataUrl
        
        // Guardar en carpeta devoluciones
        await guardarComprobante(transaccionData.id, finalPdfUrl, 'devolucion')
      } else if ((deDondeViene === "venta" || deDondeViene === "crearVenta") && tipoDeFactura === false && afipData && afipPayload) {
        // Factura en blanco (AFIP)
        const blob = await generarFacturaPdfBlob(afipData, afipPayload, getParametrosGlobales)
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        const tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 'factura'
        await guardarComprobante(transaccionData.id, dataUrl, tipoEnvio)
        await abrirFacturaPdf(afipData, afipPayload, getParametrosGlobales)
      } else {
        // Factura en negro (local)
        const pdfDataUrl = await generarFacturaPDF(transaccionData, itemsTransaccion, entidadParaFactura, tipoDeFactura, impuestos, deDondeViene, false)
        const tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 'factura'
        await guardarComprobante(transaccionData.id, (pdfDataUrl?.data?.pdfUrl || pdfDataUrl), tipoEnvio)
      }
    } catch (error) {
      console.error("Error al generar la factura PDF:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const handleDownloadRemito = async () => {
    try {
      setIsLoading(true)
      setAccionActual("Generando remito...")

      let cuentaCorrienteInfo = null
      if (deDondeViene === "venta" || deDondeViene === "crearVenta" || deDondeViene === "presupuesto") {
        try {
          const cuentas = await getCuentaCorrienteByIdEntidad(entidadParaFactura?.id)
          const cuenta = cuentas && cuentas.length > 0 ? cuentas.find((ct) => ct.idEntidad === entidadParaFactura?.id) : null
          if (cuenta) {
            cuentaCorrienteInfo = {
              saldo: cuenta.saldo,
              tieneCuentaCorriente: true
            }
          } else {
            cuentaCorrienteInfo = {
              saldo: 0,
              tieneCuentaCorriente: false
            }
          }
        } catch (error) {
          cuentaCorrienteInfo = {
            saldo: 0,
            tieneCuentaCorriente: false
          }
        }
      }

      await generarRemitoPDF(
        transaccionData,
        itemsTransaccion,
        entidadParaFactura,
        transaccionData?.usuario || {},
        tipoDeFactura,
        impuestos,
        cuentaCorrienteInfo,
      )
    } catch (error) {
      console.error("Error al generar el remito PDF:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const handlePrintTicket = async () => {
    try {
      if (usaTickeadoraActiva === false) {
        return
      }

      setIsLoading(true)
      setAccionActual("Preparando impresión...")
      
      // Obtener parámetros globales necesarios para el ticket
      let parametrosGlobales = parametrosTicket
      if (!parametrosGlobales) {
        parametrosGlobales = await obtenerParametrosTicket(getParametrosGlobales)
        setParametrosTicket(parametrosGlobales)
        const usaTickeadora = parametrosGlobales?.usaTickeadora
        if (usaTickeadoraActiva === null) {
          const habilitado = usaTickeadora === 1 || usaTickeadora === '1' || usaTickeadora === true
          setUsaTickeadoraActiva(habilitado)
          if (!habilitado) {
            setIsLoading(false)
            setAccionActual("")
            return
          }
        }
      }

      setAccionActual("Imprimiendo ticket...")
      
      // Imprimir ticket usando el nuevo servicio de impresión directa
      const result = await printerService.printTicket({
        transaccionData,
        itemsTransaccion,
        entidadParaFactura,
        parametrosGlobales
      })
      
      if (result.success) {
        setTextoDeExito("Ticket impreso correctamente")
      } else if (result.message) {
        const errorMsg = result.message
        setTextoDeExito(`Error: ${errorMsg}`)
      }

      if (result.success || result.message) {
        setTimeout(() => {
          setTextoDeExito("")
        }, 5000)
      }
      
    } catch (error) {
      console.error("Error al imprimir ticket:", error)
      setTextoDeExito(`Error: ${error.message}`)
      setTimeout(() => {
        setTextoDeExito("")
      }, 5000)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const openEmailPrompt = () => {
    // Prefill from entidad email except if generic ids 1 or 2
    const idEnt = entidadParaFactura?.id
    const prefill = idEnt === 1 || idEnt === 2 ? "" : (entidadParaFactura?.email || "")
    setEmailValue(prefill)
    setEmailError("")
    setOpenEmailModal(true)
  }

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }
  const validatePhone = (phone) => /^\+?\d{7,15}$/.test((phone || '').trim())

  const handleWhatsAppSend = async () => {
    try {
      setPhoneError("")
      if (!validatePhone(phoneValue)) {
        setPhoneError('Teléfono inválido (formato +549...)')
        return
      }
      setIsLoading(true)
      setAccionActual("Enviando WhatsApp...")

      // Obtener cuenta corriente solo si es venta, crearVenta o presupuesto
      let cuentaCorrienteInfo = null
      if (deDondeViene === "venta" || deDondeViene === "crearVenta" || deDondeViene === "presupuesto") {
        try {
          const cuentas = await getCuentaCorrienteByIdEntidad(entidadParaFactura?.id)
          const cuenta = cuentas && cuentas.length > 0 ? cuentas.find((ct) => ct.idEntidad === entidadParaFactura?.id) : null
          if (cuenta) {
            cuentaCorrienteInfo = {
              saldo: cuenta.saldo,
              tieneCuentaCorriente: true
            }
          } else {
            cuentaCorrienteInfo = {
              saldo: 0,
              tieneCuentaCorriente: false
            }
          }
        } catch (error) {
          console.error("Error al obtener cuenta corriente:", error)
          cuentaCorrienteInfo = {
            saldo: 0,
            tieneCuentaCorriente: false
          }
        }
      }

      // Generar el PDF como dataURL (igual que en Email)
      let pdfDataUrl
      let tipoEnvio = 'devolucion' // ✅ Nuevo tipo por defecto

      if (deDondeViene === "devolucion") {
        // ✅ Para devoluciones, siempre PDF local
        const out = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          true, // tipoDeFactura = true (local)
          impuestos,
          deDondeViene,
          true, // returnBase64
          cuentaCorrienteInfo
        )
        pdfDataUrl = out?.data?.pdfUrl || out
      } else if (deDondeViene === "venta" && tipoDeFactura === false && afipData && afipPayload) {
        const blob = await generarFacturaPdfBlob(afipData, afipPayload, getParametrosGlobales)
        pdfDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        tipoEnvio = 'factura'
      } else {
        const out = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          tipoDeFactura,
          impuestos,
          deDondeViene,
          true, // returnBase64
          cuentaCorrienteInfo
        )
        pdfDataUrl = out?.data?.pdfUrl || out
        tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 
                   deDondeViene === 'devolucion' ? 'devolucion' : 'factura'
      }
      const result = await enviarFacturaWhatsApp(
        transaccionData.id,
        pdfDataUrl,
        phoneValue,
        tipoEnvio
      )
      setOpenPhoneModal(false)

      if (result?.waLink) {
        window.open(result.waLink, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error("Error al enviar la factura por WhatsApp:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const handleEmailSend = async () => {
    try {
      setEmailError("")
      if (!emailValue || !validateEmail(emailValue)) {
        setEmailError("Email inválido")
        return
      }
      setIsLoading(true)
      setAccionActual("Enviando email...")
      
      // Obtener cuenta corriente solo si es venta, crearVenta o presupuesto
      let cuentaCorrienteInfo = null
      if (deDondeViene === "venta" || deDondeViene === "crearVenta" || deDondeViene === "presupuesto") {
        try {
          const cuentas = await getCuentaCorrienteByIdEntidad(entidadParaFactura?.id)
          const cuenta = cuentas && cuentas.length > 0 ? cuentas.find((ct) => ct.idEntidad === entidadParaFactura?.id) : null
          if (cuenta) {
            cuentaCorrienteInfo = {
              saldo: cuenta.saldo,
              tieneCuentaCorriente: true
            }
          } else {
            cuentaCorrienteInfo = {
              saldo: 0,
              tieneCuentaCorriente: false
            }
          }
        } catch (error) {
          console.error("Error al obtener cuenta corriente:", error)
          cuentaCorrienteInfo = {
            saldo: 0,
            tieneCuentaCorriente: false
          }
        }
      }
      
      let pdfDataUrl
      let tipoEnvio = 'devolucion' // ✅ Nuevo tipo por defecto

      if (deDondeViene === "devolucion") {
        // ✅ Para devoluciones, siempre PDF local
        pdfDataUrl = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          true, // tipoDeFactura = true (local)
          impuestos,
          deDondeViene,
          true, // returnBase64
          cuentaCorrienteInfo
        )
      } else if (deDondeViene === "venta" && tipoDeFactura === false && afipData && afipPayload) {
        // Generar PDF desde AFIP (blob -> dataURL)
        const blob = await generarFacturaPdfBlob(afipData, afipPayload, getParametrosGlobales)
        pdfDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        tipoEnvio = 'factura'
      } else {
        // Generar PDF local como dataURI, respetando el origen (venta/presupuesto/etc.)
        pdfDataUrl = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          tipoDeFactura,
          impuestos,
          deDondeViene,
          true, // returnBase64
          cuentaCorrienteInfo
        )
        tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 
                   deDondeViene === 'devolucion' ? 'devolucion' : 'factura'
      }
      console.log(`[ModalFactura] pdfBase64 prefix: ${typeof pdfDataUrl === 'string' ? pdfDataUrl.slice(0, 40) : typeof pdfDataUrl}`)
      console.log(`[ModalFactura] pdfBase64 length: ${pdfDataUrl?.length || 0}`)
      await enviarMail(transaccionData.id, pdfDataUrl, emailValue, tipoEnvio)
      setOpenEmailModal(false)
    } catch (error) {
      console.error("Error enviando email:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  const handleActualizarEmailCliente = async () => {
    try {
      setEmailError("")
      const idEnt = entidadParaFactura?.id
      if (!idEnt || idEnt === 1 || idEnt === 2) {
        setEmailError("No se puede actualizar email para entidad genérica")
        return
      }
      if (!emailValue || !validateEmail(emailValue)) {
        setEmailError("Email inválido")
        return
      }
      setIsLoading(true)
      setAccionActual("Actualizando email del cliente...")
      await updateEntidadEmail(idEnt, emailValue)
      setAccionActual("")
    } catch (error) {
      console.error("Error actualizando email del cliente:", error)
      setEmailError("No se pudo actualizar el email del cliente")
    } finally {
      setIsLoading(false)
    }
  }

  const handleWhatsApp = async () => {
    try {
      setIsLoading(true)
      setAccionActual("Enviando WhatsApp...")

      // Obtener cuenta corriente solo si es venta, crearVenta o presupuesto
      let cuentaCorrienteInfo = null
      if (deDondeViene === "venta" || deDondeViene === "crearVenta" || deDondeViene === "presupuesto") {
        try {
          const cuentas = await getCuentaCorrienteByIdEntidad(entidadParaFactura?.id)
          const cuenta = cuentas && cuentas.length > 0 ? cuentas.find((ct) => ct.idEntidad === entidadParaFactura?.id) : null
          if (cuenta) {
            cuentaCorrienteInfo = {
              saldo: cuenta.saldo,
              tieneCuentaCorriente: true
            }
          } else {
            cuentaCorrienteInfo = {
              saldo: 0,
              tieneCuentaCorriente: false
            }
          }
        } catch (error) {
          console.error("Error al obtener cuenta corriente:", error)
          cuentaCorrienteInfo = {
            saldo: 0,
            tieneCuentaCorriente: false
          }
        }
      }

      // Generar el PDF como dataURL (igual que en Email)
      let pdfDataUrl
      let tipoEnvio = 'devolucion' // ✅ Nuevo tipo por defecto

      if (deDondeViene === "devolucion") {
        // ✅ Para devoluciones, siempre PDF local
        pdfDataUrl = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          true, // tipoDeFactura = true (local)
          impuestos,
          deDondeViene,
          true, // returnBase64
          cuentaCorrienteInfo
        )
        pdfDataUrl = pdfDataUrl?.data?.pdfUrl || pdfDataUrl
      } else if (deDondeViene === "venta" && tipoDeFactura === false && afipData && afipPayload) {
        const blob = await generarFacturaPdfBlob(afipData, afipPayload, getParametrosGlobales)
        pdfDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        tipoEnvio = 'factura'
      } else {
        pdfDataUrl = await generarFacturaPDF(
          transaccionData,
          itemsTransaccion,
          entidadParaFactura,
          tipoDeFactura,
          impuestos,
          deDondeViene,
          true, // returnBase64
          cuentaCorrienteInfo
        )
        pdfDataUrl = pdfDataUrl?.data?.pdfUrl || pdfDataUrl
         tipoEnvio = deDondeViene === 'presupuesto' ? 'presupuesto' : 
                   deDondeViene === 'devolucion' ? 'devolucion' : 'factura'
      }

     const phone = entidadParaFactura?.telefono || ""

      const result = await enviarFacturaWhatsApp(
        transaccionData.id,
        pdfDataUrl,
        phone,
        tipoEnvio
      )

      // Abrimos el chat de WhatsApp del usuario con el mensaje prellenado
      if (result?.waLink) {
        window.open(result.waLink, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error("Error al enviar la factura por WhatsApp:", error)
    } finally {
      setIsLoading(false)
      setAccionActual("")
    }
  }

  return (
    <>
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: isMobileOrTablet ? 2 : 4,
          padding: isMobileOrTablet ? 2 : 3.75,
          backgroundColor: "white",
          margin: "auto",
          marginTop: isMobileOrTablet ? "5%" : "10%",
          width: isMobileOrTablet ? "95%" : "60%",
          maxWidth: isMobileOrTablet ? "100%" : 800,
          boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
          position: "relative",
          maxHeight: isMobileOrTablet ? "90vh" : "auto",
          overflow: "auto",
        }}
      >
        {/* Botón para cerrar el modal */}
        <IconButton
          className="colorGeneralParaIconos"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: isMobileOrTablet ? 8 : 12,
            right: isMobileOrTablet ? 8 : 12,
            color: "#757575",
            backgroundColor: "#f5f5f5",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
            zIndex: 1,
          }}
        >
          <CloseIcon fontSize={isMobileOrTablet ? "small" : "medium"} />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            position: "relative",
          }}
        >
          <Box
            component="h1"
            sx={{
              marginBottom: isMobileOrTablet ? 1.5 : 2.5,
              color: "#4CAF50",
              fontSize: isMobileOrTablet ? "1.5rem" : "2rem",
              fontWeight: 600,
              textAlign: "center",
              px: isMobileOrTablet ? 2 : 0,
            }}
          >
            {textoDeExito}
          </Box>

          <DotLottieReact
            src="https://lottie.host/3bf4b8df-b7cf-4d60-838a-ad9013672347/bpnBcXsF0f.lottie"
            loop={false}
            autoplay
            style={{ 
              width: isMobileOrTablet ? 160 : 240, 
              marginBottom: isMobileOrTablet ? 1.5 : 3.75 
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: isMobileOrTablet ? 1.5 : 3,
              width: "100%",
              flexDirection: isMobileOrTablet ? "column" : "row",
            }}
          >
            {/* Opciones de Descarga */}
            <Box
              sx={{
                flex: 1,
                padding: isMobileOrTablet ? 1.5 : 3,
                borderRadius: isMobileOrTablet ? 1.5 : 1.5,
                backgroundColor: "#f9f9f9",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #eaeaea",
              }}
            >
              <Box
                component="h2"
                sx={{
                  marginBottom: isMobileOrTablet ? 1.25 : 2.5,
                  color: "#333",
                  fontSize: isMobileOrTablet ? "1rem" : "1.25rem",
                  fontWeight: 500,
                  borderBottom: "2px solid #e0e0e0",
                  paddingBottom: isMobileOrTablet ? 0.75 : 1.25,
                }}
              >
                Opciones
              </Box>

              {usaTickeadoraActiva && (
                <Button
                  onClick={handlePrintTicket}
                  variant="contained"
                  color="success"
                  startIcon={<ConfirmationNumberIcon fontSize={isMobileOrTablet ? "small" : "medium"} />}
                  size={isMobileOrTablet ? "small" : "medium"}
                  sx={{
                    marginBottom: isMobileOrTablet ? 1 : 2,
                    padding: isMobileOrTablet ? "8px 12px" : "12px 16px",
                    borderRadius: 1,
                    fontWeight: 500,
                    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                    fontSize: isMobileOrTablet ? "0.875rem" : "1rem",
                  }}
                  disabled={isLoading}
                  fullWidth={isMobileOrTablet}
                >
                  Imprimir Ticket
                </Button>
              )}

              <Button
                onClick={handleDownloadPdf}
                variant="contained"
                color="primary"
                startIcon={<PictureAsPdfIcon fontSize={isMobileOrTablet ? "small" : "medium"} />}
                size={isMobileOrTablet ? "small" : "medium"}
                sx={{
                  marginBottom: isMobileOrTablet ? 1 : 2,
                  padding: isMobileOrTablet ? "8px 12px" : "12px 16px",
                  borderRadius: 1,
                  fontWeight: 500,
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                  fontSize: isMobileOrTablet ? "0.875rem" : "1rem",
                }}
                disabled={isLoading}
                fullWidth={isMobileOrTablet}
              >
                Descargar PDF
              </Button>

              {deDondeViene != "compra" ? (
                <Button
                  onClick={handleDownloadRemito}
                  variant="contained"
                  color="primary"
                  startIcon={<ReceiptLongIcon fontSize={isMobileOrTablet ? "small" : "medium"} />}
                  size={isMobileOrTablet ? "small" : "medium"}
                  sx={{
                    padding: isMobileOrTablet ? "8px 12px" : "12px 16px",
                    borderRadius: 1,
                    fontWeight: 500,
                    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                    fontSize: isMobileOrTablet ? "0.875rem" : "1rem",
                  }}
                  disabled={isLoading}
                  fullWidth={isMobileOrTablet}
                >
                  Descargar Remito
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  disabled
                  startIcon={<ReceiptLongIcon fontSize={isMobileOrTablet ? "small" : "medium"} />}
                  size={isMobileOrTablet ? "small" : "medium"}
                  sx={{
                    padding: isMobileOrTablet ? "8px 12px" : "12px 16px",
                    borderRadius: 1,
                    opacity: 0.7,
                    fontSize: isMobileOrTablet ? "0.875rem" : "1rem",
                  }}
                  fullWidth={isMobileOrTablet}
                >
                  Descargar Remito
                </Button>
              )}
            </Box>

            {/* Opciones de Envío */}
            <Box
              sx={{
                flex: 1,
                padding: isMobileOrTablet ? 1.5 : 3,
                borderRadius: isMobileOrTablet ? 1.5 : 1.5,
                backgroundColor: "#f9f9f9",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #eaeaea",
              }}
            >
              <Box
                component="h2"
                sx={{
                  marginBottom: isMobileOrTablet ? 1.25 : 2.5,
                  color: "#333",
                  fontSize: isMobileOrTablet ? "1rem" : "1.25rem",
                  fontWeight: 500,
                  borderBottom: "2px solid #e0e0e0",
                  paddingBottom: isMobileOrTablet ? 0.75 : 1.25,
                }}
              >
                Opciones de envío
              </Box>

              <Button
                onClick={openEmailPrompt}
                variant="outlined"
                startIcon={<EmailIcon fontSize={isMobileOrTablet ? "small" : "medium"} />}
                size={isMobileOrTablet ? "small" : "medium"}
                sx={{
                  marginBottom: isMobileOrTablet ? 1 : 2,
                  padding: isMobileOrTablet ? "8px 12px" : "12px 16px",
                  borderRadius: 1,
                  fontWeight: 500,
                  borderWidth: 2,
                  fontSize: isMobileOrTablet ? "0.875rem" : "1rem",
                }}
                disabled={isLoading}
                fullWidth={isMobileOrTablet}
              >
                Enviar por Email
              </Button>

              <Button
                onClick={() => {
                  const idEnt = entidadParaFactura?.id
                  const prefill = idEnt === 1 ? '' : (entidadParaFactura?.telefono || '')
                  setPhoneValue(prefill)
                  setPhoneError('')
                  setOpenPhoneModal(true)
                }}
                variant="outlined"
                startIcon={<WhatsAppIcon sx={{ color: "#25D366" }} fontSize={isMobileOrTablet ? "small" : "medium"} />}
                size={isMobileOrTablet ? "small" : "medium"}
                sx={{
                  padding: isMobileOrTablet ? "8px 12px" : "12px 16px",
                  borderRadius: 1,
                  fontWeight: 500,
                  borderWidth: 2,
                  fontSize: isMobileOrTablet ? "0.875rem" : "1rem",
                }}
                disabled={isLoading}
                fullWidth={isMobileOrTablet}
              >
                Enviar por WhatsApp
              </Button>
            </Box>
          </Box>

          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: isMobileOrTablet ? 2 : 4,
              }}
            >
              <CircularProgress size={isMobileOrTablet ? 40 : 60} thickness={4} />
              <Box
                component="p"
                sx={{
                  marginTop: isMobileOrTablet ? 1 : 2,
                  fontSize: isMobileOrTablet ? "0.875rem" : "1.125rem",
                  fontWeight: 500,
                  color: "#333",
                  textAlign: "center",
                  px: isMobileOrTablet ? 2 : 0,
                }}
              >
                {accionActual}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>

    {/* Modal para ingresar teléfono destino (WhatsApp) */}
    <Modal open={openPhoneModal} onClose={() => setOpenPhoneModal(false)}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: isMobileOrTablet ? 1 : 1.5,
          borderRadius: isMobileOrTablet ? 1.5 : 1.5,
          padding: isMobileOrTablet ? 1.5 : 2.5,
          backgroundColor: "white",
          margin: "auto",
          marginTop: isMobileOrTablet ? "10%" : "15%",
          width: isMobileOrTablet ? "90%" : 420,
          maxWidth: isMobileOrTablet ? "100%" : 420,
          boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
          position: "relative",
        }}
      >
        <Box component="h3" sx={{ margin: 0, fontSize: isMobileOrTablet ? "1rem" : "1.25rem", fontWeight: 600 }}>
          Enviar por WhatsApp
        </Box>
        <TextField
          label="Teléfono del cliente (ej: +549...)"
          value={phoneValue}
          onChange={(e) => setPhoneValue(e.target.value)}
          error={!!phoneError}
          helperText={phoneError || ""}
          size={isMobileOrTablet ? "small" : "medium"}
          fullWidth
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexDirection: isMobileOrTablet ? "column" : "row" }}>
          <Button variant="contained" onClick={async () => { await (async ()=>{ /* send */ })(); }} sx={{ display: 'none' }}>hidden</Button>
          <Button 
            variant="contained" 
            onClick={handleWhatsAppSend} 
            disabled={isLoading}
            fullWidth={isMobileOrTablet}
            size={isMobileOrTablet ? "small" : "medium"}
          >
            Enviar
          </Button>
        </Box>
      </Box>
    </Modal>

    {/* Modal pequeño para ingresar email destino */}
    <Modal open={openEmailModal} onClose={() => setOpenEmailModal(false)}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: isMobileOrTablet ? 1 : 1.5,
          borderRadius: isMobileOrTablet ? 1.5 : 1.5,
          padding: isMobileOrTablet ? 1.5 : 2.5,
          backgroundColor: "white",
          margin: "auto",
          marginTop: isMobileOrTablet ? "10%" : "15%",
          width: isMobileOrTablet ? "90%" : 420,
          maxWidth: isMobileOrTablet ? "100%" : 420,
          boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
          position: "relative",
        }}
      >
        <Box component="h3" sx={{ margin: 0, fontSize: isMobileOrTablet ? "1rem" : "1.25rem", fontWeight: 600 }}>
          Enviar factura por email
        </Box>
        <TextField
          label="Email del cliente"
          value={emailValue}
          onChange={(e) => setEmailValue(e.target.value)}
          error={!!emailError}
          helperText={emailError || ""}
          size={isMobileOrTablet ? "small" : "medium"}
          fullWidth
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexDirection: isMobileOrTablet ? "column" : "row" }}>
          <Button 
            variant="outlined" 
            onClick={handleActualizarEmailCliente} 
            disabled={isLoading}
            fullWidth={isMobileOrTablet}
            size={isMobileOrTablet ? "small" : "medium"}
            sx={{ fontSize: isMobileOrTablet ? "0.75rem" : "0.875rem" }}
          >
            Actualizar email cliente
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEmailSend} 
            disabled={isLoading}
            fullWidth={isMobileOrTablet}
            size={isMobileOrTablet ? "small" : "medium"}
          >
            Enviar
          </Button>
        </Box>
      </Box>
    </Modal>
    </>
  )
}

export default ModalOpcionesFactura
